import { Fragment, type ReactNode } from 'react';

/** Negrito (**x**) e código (`x`) em uma linha; o restante é texto puro. */
function inline(text: string, key: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={`${key}-${i}`}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`'))
      return (
        <code key={`${key}-${i}`} className="rounded bg-muted px-1 font-mono text-[12px]">
          {part.slice(1, -1)}
        </code>
      );
    return <Fragment key={`${key}-${i}`}>{part}</Fragment>;
  });
}

/**
 * Renderizador mínimo do texto do assistente: parágrafos, listas com "-"/"*" ou numeradas,
 * negrito e código. Sem HTML bruto, sem dependências.
 */
export function AssistantMarkdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.replace(/\r/g, '').split('\n');
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) {
      const key = `p${blocks.length}`;
      blocks.push(
        <p key={key} className="mb-2 last:mb-0">
          {para.map((l, i) => (
            <Fragment key={i}>
              {i > 0 ? <br /> : null}
              {inline(l, `${key}-${i}`)}
            </Fragment>
          ))}
        </p>,
      );
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const key = `l${blocks.length}`;
      const items = list.items.map((it, i) => <li key={i}>{inline(it, `${key}-${i}`)}</li>);
      blocks.push(
        list.ordered ? (
          <ol key={key} className="mb-2 list-decimal space-y-0.5 pl-5 last:mb-0">
            {items}
          </ol>
        ) : (
          <ul key={key} className="mb-2 list-disc space-y-0.5 pl-5 last:mb-0">
            {items}
          </ul>
        ),
      );
      list = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    const bullet = /^[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushPara();
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push((bullet ?? numbered)![1]!);
    } else if (line === '') {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.replace(/^#+\s*/, ''));
    }
  }
  flushPara();
  flushList();
  return <div className="text-sm leading-relaxed">{blocks}</div>;
}
